import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getClients,
    getClientsSummary,
    getBranchExpenses,
    getPendingPurchases,
    getPurchasePaymentsByDate,
    getPurchasesByDate,
    getStockLists
} from "../api";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from "chart.js";
import DateRangePresetPicker from "./DateRangePresetPicker";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "./InlineLoading";
import ReportBranchPicker from "./ReportBranchPicker";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { getRangeFromDays } from "../utils/datePresets";
import { resolveProviderTotalAmount, resolveStockCostAmount } from "../utils/stockCost";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const KNOWN_PAYMENT_METHODS = ["efectivo", "transferencia", "tarjeta", "cheque"];
const PAYMENT_METHODS = [...KNOWN_PAYMENT_METHODS, "otros"];

const buildMethodTotals = () => PAYMENT_METHODS.reduce((acc, method) => {
    acc[method] = 0;
    return acc;
}, {});

const addMethodAmount = (bucket, method, amount) => {
    const normalized = method && KNOWN_PAYMENT_METHODS.includes(method) ? method : "otros";
    bucket[normalized] = (bucket[normalized] || 0) + amount;
};

const getStockCashEntries = (stock, fallbackDate) => {
    const payments = Array.isArray(stock?.StockPayments)
        ? stock.StockPayments.filter((payment) => !payment.isDeleted)
        : [];
    if (payments.length === 0) return [];
    return payments
        .map((payment) => ({
            method: payment.method,
            amount: Number(payment.amount || 0),
            date: payment.paidAt || payment.createdAt || fallbackDate,
        }))
        .filter((entry) => Number.isFinite(entry.amount) && entry.amount > 0);
};

const getStockAccrualEntries = (stock, fallbackDate) => {
    const total = resolveStockCostAmount(stock);
    if (!Number.isFinite(total) || total <= 0) return [];
    return [{
        method: stock.paymentMethod || "otros",
        amount: total,
        date: fallbackDate,
    }];
};

const Finance = () => {
    const { branchId: routeBranchId } = useParams();
    const storedScope = localStorage.getItem("reportBranchScope");
    const defaultBranchScope =
        storedScope || routeBranchId || localStorage.getItem("selectedBranchId") || "";
    const [branchScopeId, setBranchScopeId] = useState(defaultBranchScope);
    const { user } = useCurrentUser();
    const canSelectBranch = hasPermission(user, "branch.access.any");
    const todayRange = useMemo(() => getRangeFromDays(1), []);
    const [startDate, setStartDate] = useState(todayRange.startDate);
    const [endDate, setEndDate] = useState(todayRange.endDate);
    const [basis, setBasis] = useState("cash");

    const [loading, setLoading] = useState(true);
    const [expensesTotal, setExpensesTotal] = useState(0);
    const [cashSalesTotal, setCashSalesTotal] = useState(0);
    const [accrualSalesTotal, setAccrualSalesTotal] = useState(0);
    const [cashStockTotal, setCashStockTotal] = useState(0);
    const [accrualStockTotal, setAccrualStockTotal] = useState(0);

    const [expenses, setExpenses] = useState([]);
    const [purchasePayments, setPurchasePayments] = useState([]);
    const [accrualPurchases, setAccrualPurchases] = useState([]);
    const [stockLists, setStockLists] = useState([]);
    const [allStockLists, setAllStockLists] = useState([]);
    const [openPurchases, setOpenPurchases] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientSummaries, setClientSummaries] = useState([]);

    const [groupedExpensesTotals, setGroupedExpensesTotals] = useState({});
    const [groupedCashSalesTotals, setGroupedCashSalesTotals] = useState({});
    const [groupedAccrualSalesTotals, setGroupedAccrualSalesTotals] = useState({});
    const [groupedCashStockTotals, setGroupedCashStockTotals] = useState({});
    const [groupedAccrualStockTotals, setGroupedAccrualStockTotals] = useState({});
    const [groupedCashTotals, setGroupedCashTotals] = useState({});
    const [groupedAccrualTotals, setGroupedAccrualTotals] = useState({});

    const [expensesDataSet, setExpensesDataSet] = useState([]);
    const [cashSalesDataSet, setCashSalesDataSet] = useState([]);
    const [accrualSalesDataSet, setAccrualSalesDataSet] = useState([]);
    const [cashStockDataSet, setCashStockDataSet] = useState([]);
    const [accrualStockDataSet, setAccrualStockDataSet] = useState([]);
    const [cashTotalDataSet, setCashTotalDataSet] = useState({});
    const [accrualTotalDataSet, setAccrualTotalDataSet] = useState({});

    const [selectedCategory, setSelectedCategory] = useState("total");
    const [selectedDataSet, setSelectedDataSet] = useState([]);

    useEffect(() => {
        if (branchScopeId) {
            localStorage.setItem("reportBranchScope", branchScopeId);
        }
    }, [branchScopeId]);

    useEffect(() => {
        if (!canSelectBranch && branchScopeId === "all") {
            const fallback = localStorage.getItem("selectedBranchId") || "";
            setBranchScopeId(fallback);
        }
    }, [canSelectBranch, branchScopeId]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (!branchScopeId) return;
                if (branchScopeId === "all" && !canSelectBranch) return;
                const clientSummaryParams = { pageSize: 0 };
                if (canSelectBranch && branchScopeId) {
                    clientSummaryParams.branchId = branchScopeId;
                }
                const [
                    expensesData,
                    purchasePaymentsData,
                    accrualPurchasesData,
                    stockListsData,
                    allStockListsData,
                    openPurchasesData,
                    clientsData,
                    clientSummariesData,
                ] = await Promise.all([
                    getBranchExpenses(branchScopeId, startDate, endDate, { pageSize: 0 }),
                    getPurchasePaymentsByDate(branchScopeId, startDate, endDate, { pageSize: 0 }),
                    getPurchasesByDate(branchScopeId, startDate, endDate, { pageSize: 0 }),
                    getStockLists(branchScopeId, startDate, endDate, { pageSize: 0 }),
                    getStockLists(branchScopeId, undefined, undefined, { pageSize: 0 }),
                    getPendingPurchases(branchScopeId, undefined, undefined, undefined, undefined, { pageSize: 0 }),
                    getClients({ pageSize: 0 }),
                    getClientsSummary(clientSummaryParams),
                ]);
                const normalizedPayments = normalizePaginatedResponse(purchasePaymentsData);
                const normalizedPurchases = normalizePaginatedResponse(accrualPurchasesData);
                const normalizedOpenPurchases = normalizePaginatedResponse(openPurchasesData);
                const closedAccrualPurchases = (normalizedPurchases.data || []).filter(
                    (purchase) => purchase.saleStatus === "completed"
                );
                const closedPayments = (normalizedPayments.data || []).filter(
                    (payment) => payment?.Purchase?.saleStatus === "completed"
                );
                const normalizedExpenses = normalizePaginatedResponse(expensesData).data || [];
                const normalizedStockLists = normalizePaginatedResponse(stockListsData).data || [];
                const normalizedAllStockLists = normalizePaginatedResponse(allStockListsData).data || [];
                const normalizedClients = normalizePaginatedResponse(clientsData).data || [];
                const normalizedClientSummaries = normalizePaginatedResponse(clientSummariesData).data || [];

                setExpenses(normalizedExpenses);
                setPurchasePayments(closedPayments);
                setAccrualPurchases(closedAccrualPurchases);
                setStockLists(normalizedStockLists);
                setAllStockLists(normalizedAllStockLists);
                setOpenPurchases(normalizedOpenPurchases.data || []);
                setClients(normalizedClients);
                setClientSummaries(normalizedClientSummaries);

                setExpensesTotal(normalizedExpenses.reduce((sum, e) => sum + e.amount, 0));
                const cashSales = closedPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
                setCashSalesTotal(cashSales);
                const accrualSales = closedAccrualPurchases.reduce(
                    (sum, purchase) => sum + Number(purchase.totalAmount || 0),
                    0
                );
                setAccrualSalesTotal(accrualSales);

                const cashStock = normalizedStockLists.reduce((acc, stockList) => {
                    const listPayments = stockList?.Stocks?.flatMap((stock) =>
                        getStockCashEntries(stock, stockList.createdAt)
                    ) || [];
                    const listTotal = listPayments.reduce((sum, entry) => sum + entry.amount, 0);
                    return acc + listTotal;
                }, 0);
                setCashStockTotal(cashStock);

                const accrualStock = normalizedStockLists.reduce((acc, stockList) => {
                    const listEntries = stockList?.Stocks?.flatMap((stock) =>
                        getStockAccrualEntries(stock, stockList.createdAt)
                    ) || [];
                    const listTotal = listEntries.reduce((sum, entry) => sum + entry.amount, 0);
                    return acc + listTotal;
                }, 0);
                setAccrualStockTotal(accrualStock);
            } catch (error) {
                console.error("Error fetching finance data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [branchScopeId, startDate, endDate, canSelectBranch]);

    useEffect(() => {
        const groupDataByPaymentMethod = () => {
            const groupedExpenses = buildMethodTotals();
            expenses.forEach((expense) => {
                addMethodAmount(groupedExpenses, expense.paymentMethod, expense.amount);
            });

            const groupedCashSales = buildMethodTotals();
            purchasePayments.forEach((payment) => {
                addMethodAmount(groupedCashSales, payment.method, Number(payment.amount || 0));
            });

            const groupedAccrualSales = buildMethodTotals();
            accrualPurchases.forEach((purchase) => {
                addMethodAmount(groupedAccrualSales, purchase.paymentMethod, Number(purchase.totalAmount || 0));
            });

            const groupedCashStock = buildMethodTotals();
            stockLists.forEach((stockList) => {
                stockList?.Stocks?.forEach((stock) => {
                    const payments = getStockCashEntries(stock, stockList.createdAt);
                    payments.forEach((payment) => {
                        addMethodAmount(groupedCashStock, payment.method, payment.amount);
                    });
                });
            });

            const groupedAccrualStock = buildMethodTotals();
            stockLists.forEach((stockList) => {
                stockList?.Stocks?.forEach((stock) => {
                    const entries = getStockAccrualEntries(stock, stockList.createdAt);
                    entries.forEach((entry) => {
                        addMethodAmount(groupedAccrualStock, entry.method, entry.amount);
                    });
                });
            });

            const groupedCashTotals = buildMethodTotals();
            PAYMENT_METHODS.forEach((method) => {
                groupedCashTotals[method] = (groupedCashSales[method] || 0) - (groupedCashStock[method] || 0) - (groupedExpenses[method] || 0);
            });

            setGroupedExpensesTotals(groupedExpenses);
            setGroupedCashSalesTotals(groupedCashSales);
            setGroupedAccrualSalesTotals(groupedAccrualSales);
            setGroupedCashStockTotals(groupedCashStock);
            setGroupedAccrualStockTotals(groupedAccrualStock);
            setGroupedCashTotals(groupedCashTotals);

            const groupedAccrualTotals = buildMethodTotals();
            PAYMENT_METHODS.forEach((method) => {
                groupedAccrualTotals[method] = (groupedAccrualSales[method] || 0) - (groupedAccrualStock[method] || 0) - (groupedExpenses[method] || 0);
            });
            setGroupedAccrualTotals(groupedAccrualTotals);
        }
        groupDataByPaymentMethod();
    }, [expenses, purchasePayments, accrualPurchases, stockLists]);

    useEffect(() => {
        // Get the total of money for each date from the startDate to endDate, inside that for each payment method get the totals
        const getTotalDataSet = () => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const expensesData = {};
            const cashSalesData = {};
            const accrualSalesData = {};
            const cashStockData = {};
            const accrualStockData = {};
            const cashTotalData = {};
            const accrualTotalData = {};

            for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
                const dateString = getDateString(d);
                expensesData[dateString] = { ...buildMethodTotals(), total: 0 };
                cashSalesData[dateString] = { ...buildMethodTotals(), total: 0 };
                accrualSalesData[dateString] = { ...buildMethodTotals(), total: 0 };
                cashStockData[dateString] = { ...buildMethodTotals(), total: 0 };
                accrualStockData[dateString] = { ...buildMethodTotals(), total: 0 };
                cashTotalData[dateString] = { ...buildMethodTotals(), total: 0 };
                accrualTotalData[dateString] = { ...buildMethodTotals(), total: 0 };
            }

            expenses.forEach((expense) => {
                const dateString = getDateString(expense.date);
                if (cashTotalData[dateString]) {
                    addMethodAmount(expensesData[dateString], expense.paymentMethod, expense.amount);
                    expensesData[dateString]["total"] += expense.amount;
                    addMethodAmount(cashTotalData[dateString], expense.paymentMethod, -expense.amount);
                    cashTotalData[dateString]["total"] -= expense.amount;
                    addMethodAmount(accrualTotalData[dateString], expense.paymentMethod, -expense.amount);
                    accrualTotalData[dateString]["total"] -= expense.amount;
                }
            });

            purchasePayments.forEach((payment) => {
                const dateString = getDateString(payment.paidAt || payment.createdAt);
                if (cashTotalData[dateString]) {
                    const amount = Number(payment.amount || 0);
                    addMethodAmount(cashSalesData[dateString], payment.method, amount);
                    cashSalesData[dateString]["total"] += amount;
                    addMethodAmount(cashTotalData[dateString], payment.method, amount);
                    cashTotalData[dateString]["total"] += amount;
                }
            });

            accrualPurchases.forEach((purchase) => {
                const dateString = getDateString(purchase.createdAt);
                if (accrualTotalData[dateString]) {
                    const amount = Number(purchase.totalAmount || 0);
                    addMethodAmount(accrualSalesData[dateString], purchase.paymentMethod, amount);
                    accrualSalesData[dateString]["total"] += amount;
                    addMethodAmount(accrualTotalData[dateString], purchase.paymentMethod, amount);
                    accrualTotalData[dateString]["total"] += amount;
                }
            });

            stockLists.forEach((stockList) => {
                stockList?.Stocks?.forEach((stock) => {
                    const payments = getStockCashEntries(stock, stockList.createdAt);
                    payments.forEach((payment) => {
                        const dateString = getDateString(payment.date || stockList.createdAt);
                        if (cashTotalData[dateString]) {
                            addMethodAmount(cashStockData[dateString], payment.method, payment.amount);
                            cashStockData[dateString]["total"] += payment.amount;
                            addMethodAmount(cashTotalData[dateString], payment.method, -payment.amount);
                            cashTotalData[dateString]["total"] -= payment.amount;
                        }
                    });
                });
            });

            stockLists.forEach((stockList) => {
                stockList?.Stocks?.forEach((stock) => {
                    const entries = getStockAccrualEntries(stock, stockList.createdAt);
                    entries.forEach((entry) => {
                        const dateString = getDateString(entry.date || stockList.createdAt);
                        if (accrualTotalData[dateString]) {
                            addMethodAmount(accrualStockData[dateString], entry.method, entry.amount);
                            accrualStockData[dateString]["total"] += entry.amount;
                            addMethodAmount(accrualTotalData[dateString], entry.method, -entry.amount);
                            accrualTotalData[dateString]["total"] -= entry.amount;
                        }
                    });
                });
            });

            setExpensesDataSet(expensesData);
            setCashSalesDataSet(cashSalesData);
            setAccrualSalesDataSet(accrualSalesData);
            setCashStockDataSet(cashStockData);
            setAccrualStockDataSet(accrualStockData);
            setCashTotalDataSet(cashTotalData);
            setAccrualTotalDataSet(accrualTotalData);
        }
        getTotalDataSet();
    }, [expenses, purchasePayments, accrualPurchases, stockLists, startDate, endDate]);

    const getDateString = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    }

    const getSummaryData = (dataSet, paymentMethod) => {
        const labels = Object.keys(dataSet);
        const data = labels.map((label) => dataSet[label][paymentMethod]);
        return {
            labels,
            datasets: [
                {
                    label: paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1),
                    data,
                    backgroundColor: paymentMethod === "efectivo"
                        ? "rgba(75, 192, 192, 0.6)"
                        : paymentMethod === "transferencia"
                            ? "rgba(153, 102, 255, 0.6)"
                            : paymentMethod === "cheque"
                                ? "rgba(201, 203, 207, 0.6)"
                                : paymentMethod === "tarjeta"
                                    ? "rgba(255, 159, 64, 0.6)"
                                    : "rgba(107, 114, 128, 0.6)",
                },
            ],
        };
    }

    function getGeneralData(dataSet) {
        return {
            labels: Object.keys(dataSet),
            datasets: [
                {
                    label: "Total",
                    data: Object.values(dataSet).map((data) => data.total),
                    backgroundColor: "rgba(255, 99, 132, 0.6)",
                },
                {
                    label: "Efectivo",
                    data: Object.values(dataSet).map((data) => data.efectivo),
                    backgroundColor: "rgba(75, 192, 192, 0.6)",
                },
                {
                    label: "Transferencia",
                    data: Object.values(dataSet).map((data) => data.transferencia),
                    backgroundColor: "rgba(153, 102, 255, 0.6)",
                },
                {
                    label: "Tarjeta",
                    data: Object.values(dataSet).map((data) => data.tarjeta),
                    backgroundColor: "rgba(255, 159, 64, 0.6)",
                },
                {
                    label: "Cheque",
                    data: Object.values(dataSet).map((data) => data.cheque),
                    backgroundColor: "rgba(201, 203, 207, 0.6)",
                },
                {
                    label: "Otros",
                    data: Object.values(dataSet).map((data) => data.otros),
                    backgroundColor: "rgba(107, 114, 128, 0.6)",
                },
            ],
        };
    }

    const getDataSetForCategory = (category, currentBasis) => {
        if (category === "expenses") return expensesDataSet;
        if (category === "sales") {
            return currentBasis === "cash" ? cashSalesDataSet : accrualSalesDataSet;
        }
        if (category === "stock") {
            return currentBasis === "cash" ? cashStockDataSet : accrualStockDataSet;
        }
        return currentBasis === "cash" ? cashTotalDataSet : accrualTotalDataSet;
    };

    useEffect(() => {
        setSelectedDataSet(getDataSetForCategory(selectedCategory, basis));
    }, [
        selectedCategory,
        basis,
        expensesDataSet,
        cashSalesDataSet,
        accrualSalesDataSet,
        cashStockDataSet,
        accrualStockDataSet,
        cashTotalDataSet,
        accrualTotalDataSet,
    ]);

    const activeSalesTotal = basis === "cash" ? cashSalesTotal : accrualSalesTotal;
    const activeStockTotal = basis === "cash" ? cashStockTotal : accrualStockTotal;
    const activeGroupedSales = basis === "cash" ? groupedCashSalesTotals : groupedAccrualSalesTotals;
    const activeGroupedStock = basis === "cash" ? groupedCashStockTotals : groupedAccrualStockTotals;
    const activeGroupedTotal = basis === "cash" ? groupedCashTotals : groupedAccrualTotals;
    const netTotal = activeSalesTotal - expensesTotal - activeStockTotal;

    const parseDetailForPayment = (value) => {
        if (!value) return null;
        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch {
                return null;
            }
        }
        return value;
    };

    const getPurchaseDueDate = (purchase) => {
        if (!purchase) return null;
        const isCredit = purchase.paymentTerm === "credit" || purchase.paymentMethod === "crédito";
        if (!isCredit) return null;
        const detail = parseDetailForPayment(purchase.detailForPayment);
        const value = purchase.creditDueDate || detail?.creditDueDate || detail?.creditExpiration;
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date;
    };

    const receivablesSummary = useMemo(() => {
        const buckets = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
        let total = 0;
        let overdue = 0;
        const nowDate = new Date();

        openPurchases.forEach((purchase) => {
            const balance = Number(purchase.balance || 0);
            if (!Number.isFinite(balance) || balance <= 0) return;
            total += balance;
            const dueDate = getPurchaseDueDate(purchase);
            if (!dueDate || dueDate > nowDate) return;
            overdue += balance;
            const days = Math.floor((nowDate - dueDate) / (1000 * 60 * 60 * 24));
            if (days <= 30) buckets["0-30"] += balance;
            else if (days <= 60) buckets["31-60"] += balance;
            else if (days <= 90) buckets["61-90"] += balance;
            else buckets["90+"] += balance;
        });

        return { total, overdue, buckets };
    }, [openPurchases]);

    const payablesSummary = useMemo(() => {
        const buckets = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
        let total = 0;
        const nowDate = new Date();

        allStockLists.forEach((stockList) => {
            const listDate = new Date(stockList.createdAt);
            stockList?.Stocks?.forEach((stock) => {
                const balance = Number(stock.balance || 0);
                if (!Number.isFinite(balance) || balance <= 0) return;
                total += balance;
                const ageDays = Number.isNaN(listDate.getTime())
                    ? 0
                    : Math.floor((nowDate - listDate) / (1000 * 60 * 60 * 24));
                if (ageDays <= 30) buckets["0-30"] += balance;
                else if (ageDays <= 60) buckets["31-60"] += balance;
                else if (ageDays <= 90) buckets["61-90"] += balance;
                else buckets["90+"] += balance;
            });
        });

        return { total, buckets };
    }, [allStockLists]);

    const creditExposure = useMemo(() => {
        const summaryMap = clientSummaries.reduce((acc, summary) => {
            if (summary?.clientId) {
                acc[summary.clientId] = summary;
            }
            return acc;
        }, {});
        let overLimitCount = 0;
        let overLimitAmount = 0;
        let limitExposure = 0;
        let overdueClients = 0;

        clients.forEach((client) => {
            const summary = summaryMap[client.id];
            if (!summary) return;
            const balance = Number(summary.balance || 0);
            const overdue = Number(summary.overdue || 0);
            const creditLimit = Number(client.creditLimit || 0);
            if (overdue > 0) overdueClients += 1;
            if (creditLimit > 0) {
                limitExposure += balance;
                if (balance > creditLimit) {
                    overLimitCount += 1;
                    overLimitAmount += balance - creditLimit;
                }
            }
        });

        return { overLimitCount, overLimitAmount, limitExposure, overdueClients };
    }, [clients, clientSummaries]);

    const formatDateOnly = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
    };

    const escapeCsvValue = (value) => {
        if (value === null || value === undefined) return "";
        const raw = String(value);
        const escaped = raw.replace(/"/g, '""');
        if (/[",\n]/.test(escaped)) {
            return `"${escaped}"`;
        }
        return escaped;
    };

    const buildCsv = (headers, rows) => {
        const headerRow = headers.map(escapeCsvValue).join(",");
        const bodyRows = rows.map((row) => row.map(escapeCsvValue).join(","));
        return [headerRow, ...bodyRows].join("\n");
    };

    const downloadCsv = (filename, headers, rows) => {
        const content = buildCsv(headers, rows);
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportReceivables = () => {
        const headers = [
            "purchase_id",
            "client",
            "rut",
            "date",
            "total",
            "paid",
            "balance",
            "term",
            "status",
            "due_date",
            "overdue",
        ];
        const rows = openPurchases
            .filter((purchase) => Number(purchase.balance || 0) > 0)
            .map((purchase) => {
                const balance = Number(purchase.balance || 0);
                const paid = Number(purchase.paidAmount || 0);
                const total = Number(purchase.totalAmount || 0);
                const term = purchase.paymentTerm || (purchase.paymentMethod === "crédito" ? "credit" : "immediate");
                const status = purchase.paymentStatus || (purchase.isPaid ? "paid" : "unpaid");
                const dueDate = getPurchaseDueDate(purchase);
                const overdue = dueDate && dueDate < new Date() && balance > 0 ? "yes" : "no";
                return [
                    purchase.id,
                    purchase.Client?.name || "",
                    purchase.Client?.rut || "",
                    formatDateOnly(purchase.createdAt),
                    total,
                    paid,
                    balance,
                    term,
                    status,
                    formatDateOnly(dueDate),
                    overdue,
                ];
            });
        downloadCsv("receivables.csv", headers, rows);
    };

    const handleExportPayables = () => {
        const headers = [
            "stock_id",
            "stock_list",
            "provider",
            "date",
            "total",
            "paid",
            "balance",
            "method",
        ];
        const rows = [];
        allStockLists.forEach((stockList) => {
            stockList?.Stocks?.forEach((stock) => {
                const balance = Number(stock.balance || 0);
                if (!Number.isFinite(balance) || balance <= 0) return;
                const total = resolveProviderTotalAmount(stock);
                const paid = Number(stock.paidAmount || 0);
                rows.push([
                    stock.id,
                    stockList.name || "",
                    stock.Provider?.name || "",
                    formatDateOnly(stockList.createdAt),
                    total,
                    paid,
                    balance,
                    stock.paymentMethod || "",
                ]);
            });
        });
        downloadCsv("payables.csv", headers, rows);
    };

    const handleExportCashMovements = () => {
        const headers = [
            "date",
            "type",
            "method",
            "amount",
            "direction",
            "reference",
            "note",
            "source_id",
        ];
        const rows = [];

        purchasePayments.forEach((payment) => {
            rows.push([
                formatDateOnly(payment.paidAt || payment.createdAt),
                "sale_payment",
                payment.method || "",
                Number(payment.amount || 0),
                "in",
                payment.reference || "",
                payment.note || "",
                payment.Purchase?.id || "",
            ]);
        });

        expenses.forEach((expense) => {
            rows.push([
                formatDateOnly(expense.date),
                "expense",
                expense.paymentMethod || "",
                Number(expense.amount || 0),
                "out",
                "",
                expense.description || "",
                expense.id || "",
            ]);
        });

        stockLists.forEach((stockList) => {
            stockList?.Stocks?.forEach((stock) => {
                const payments = Array.isArray(stock.StockPayments)
                    ? stock.StockPayments.filter((payment) => !payment.isDeleted)
                    : [];
                payments.forEach((payment) => {
                    rows.push([
                        formatDateOnly(payment.paidAt || payment.createdAt),
                        "stock_payment",
                        payment.method || "",
                        Number(payment.amount || 0),
                        "out",
                        payment.reference || "",
                        payment.note || "",
                        stock.id,
                    ]);
                });
            });
        });

        rows.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
        downloadCsv("cash_movements.csv", headers, rows);
    };


    return (
        <div className="flex justify-center items-center min-w-screen bg-gray-50">
            <div className="p-6 max-w-6xl w-full">

                {loading ? (
                    <>
                        <h1 className="text-3xl font-bold mb-6">Finanzas</h1>
                        <p className="text-center text-lg text-gray-500">
                            <InlineLoading label="Cargando datos financieros" />
                        </p>
                    </>
                ) : (
                    <>
                        {/* Filters */}
                        <div className="mb-6 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between">
                            <h1 className="text-3xl font-bold mb-6">Finanzas</h1>
                            {canSelectBranch && (
                                <ReportBranchPicker
                                    value={branchScopeId}
                                    onChange={setBranchScopeId}
                                    className="w-full"
                                />
                            )}
                            {/* Fecha especifica */}
                            <DateRangePresetPicker
                                label="Rango de fechas"
                                presetLabel="Rango rapido"
                                presetDays={[1, 7, 31, 60, 90]}
                                presetOptionLabel={(days) => (Number(days) === 1 ? "Hoy" : `Ultimos ${days} dias`)}
                                startLabel="Desde"
                                endLabel="Hasta"
                                startDate={startDate}
                                endDate={endDate}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                                containerClassName="w-full sm:w-auto"
                                labelClassName="block text-sm font-medium mb-1"
                                inputLabelClassName="block text-xs text-gray-600 mb-1"
                                selectClassName="border rounded px-2 py-1"
                                inputClassName="border rounded px-2 py-1"
                            />
                            <div className="flex gap-2 items-end">
                                <button
                                    onClick={() => setBasis("cash")}
                                    className={`px-4 py-2 rounded ${basis === "cash" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                                >
                                    Base caja
                                </button>
                                <button
                                    onClick={() => setBasis("accrual")}
                                    className={`px-4 py-2 rounded ${basis === "accrual" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                                >
                                    Base devengo
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-6">
                            <button
                                onClick={handleExportReceivables}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Exportar AR
                            </button>
                            <button
                                onClick={handleExportPayables}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Exportar AP
                            </button>
                            <button
                                onClick={handleExportCashMovements}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Exportar caja
                            </button>
                        </div>

                        {/* AR/AP Summary */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 sm:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white p-4 rounded shadow">
                                <h2 className="text-lg font-semibold">Por cobrar</h2>
                                <p className="text-2xl font-bold">${formatPesoAmount(receivablesSummary.total)}</p>
                                <p className="text-sm text-red-600">Vencido: ${formatPesoAmount(receivablesSummary.overdue)}</p>
                            </div>
                            <div className="bg-white p-4 rounded shadow">
                                <h2 className="text-lg font-semibold">Por pagar</h2>
                                <p className="text-2xl font-bold">${formatPesoAmount(payablesSummary.total)}</p>
                                <p className="text-sm text-gray-600">Buckets por antiguedad</p>
                            </div>
                            <div className="bg-white p-4 rounded shadow">
                            <h2 className="text-lg font-semibold">Clientes sobre limite</h2>
                                <p className="text-2xl font-bold">{creditExposure.overLimitCount}</p>
                                <p className="text-sm text-red-600">Sobre limite: ${formatPesoAmount(creditExposure.overLimitAmount)}</p>
                            </div>
                            <div className="bg-white p-4 rounded shadow">
                                <h2 className="text-lg font-semibold">Clientes vencidos</h2>
                                <p className="text-2xl font-bold">{creditExposure.overdueClients}</p>
                                <p className="text-sm text-gray-600">Exposicion con limite: ${formatPesoAmount(creditExposure.limitExposure)}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white p-4 rounded shadow">
                                <h3 className="text-lg font-semibold mb-3">Cobranza vencida (dias)</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between">
                                        <span>0-30</span>
                                        <span>${formatPesoAmount(receivablesSummary.buckets["0-30"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>31-60</span>
                                        <span>${formatPesoAmount(receivablesSummary.buckets["31-60"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>61-90</span>
                                        <span>${formatPesoAmount(receivablesSummary.buckets["61-90"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>90+</span>
                                        <span>${formatPesoAmount(receivablesSummary.buckets["90+"])}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded shadow">
                                <h3 className="text-lg font-semibold mb-3">Pagos pendientes (dias)</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between">
                                        <span>0-30</span>
                                        <span>${formatPesoAmount(payablesSummary.buckets["0-30"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>31-60</span>
                                        <span>${formatPesoAmount(payablesSummary.buckets["31-60"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>61-90</span>
                                        <span>${formatPesoAmount(payablesSummary.buckets["61-90"])}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>90+</span>
                                        <span>${formatPesoAmount(payablesSummary.buckets["90+"])}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 sm:grid-cols-2 gap-6 mb-8">
                            <div
                                className="bg-green-100 text-green-800 p-4 rounded shadow cursor-pointer"
                                onClick={() => {
                                    setSelectedCategory("stock");
                                    setSelectedDataSet(getDataSetForCategory("stock", basis));
                                }}
                            >
                                <h2 className="text-xl font-semibold">Total Compras</h2>
                                <p className="text-2xl font-bold mb-4">${formatPesoAmount(activeStockTotal)}</p>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Transferencias:</p>
                                    <p className="text-sm font-semibold">${formatPesoAmount(activeGroupedStock["transferencia"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Efectivo:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedStock["efectivo"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Cheque:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedStock["cheque"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Otros:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedStock["otros"]) || 0}</p>
                                </div>
                            </div>

                            <div
                                className="bg-red-100 text-red-800 p-4 rounded shadow cursor-pointer"
                                onClick={() => {
                                    setSelectedCategory("expenses");
                                    setSelectedDataSet(getDataSetForCategory("expenses", basis));
                                }}
                            >
                                <h2 className="text-xl font-semibold">Total Gastos</h2>
                                <p className="text-2xl font-bold mb-4">${formatPesoAmount(expensesTotal)}</p>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Transferencias:</p>
                                    <p className="text-sm font-semibold">${formatPesoAmount(groupedExpensesTotals["transferencia"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Efectivo:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(groupedExpensesTotals["efectivo"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Tarjeta:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(groupedExpensesTotals["tarjeta"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Cheque:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(groupedExpensesTotals["cheque"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Otros:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(groupedExpensesTotals["otros"]) || 0}</p>
                                </div>

                            </div>
                            <div
                                className="bg-blue-100 text-blue-800 p-4 rounded shadow cursor-pointer"
                                onClick={() => {
                                    setSelectedCategory("sales");
                                    setSelectedDataSet(getDataSetForCategory("sales", basis));
                                }}
                            >
                                <h2 className="text-xl font-semibold">Total Ventas</h2>
                                <p className="text-2xl font-bold mb-4">${formatPesoAmount(activeSalesTotal)}</p>

                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Transferencias:</p>
                                    <p className="text-sm font-semibold">${formatPesoAmount(activeGroupedSales["transferencia"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Efectivo:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedSales["efectivo"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Tarjeta:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedSales["tarjeta"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Cheque:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedSales["cheque"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Otros:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedSales["otros"]) || 0}</p>
                                </div>
                            </div>
                            <div
                                className="bg-gray-200 text-gray-800 p-4 rounded shadow cursor-pointer"
                                onClick={() => {
                                    setSelectedCategory("total");
                                    setSelectedDataSet(getDataSetForCategory("total", basis));
                                }}
                            >
                                <h2 className="text-xl font-semibold">Total</h2>
                                <p className="text-2xl font-bold mb-4">${formatPesoAmount(netTotal)}</p>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Transferencias:</p>
                                    <p className="text-sm font-semibold">${formatPesoAmount(activeGroupedTotal["transferencia"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Efectivo:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedTotal["efectivo"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Tarjeta:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedTotal["tarjeta"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Cheque:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedTotal["cheque"]) || 0}</p>
                                </div>
                                <div className="justify-between flex">
                                    <p className="text-sm font-semibold">Otros:</p>
                                    <p className="text-sm font-semibold"> ${formatPesoAmount(activeGroupedTotal["otros"]) || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="bg-white p-6 rounded shadow">
                            <h2 className="text-xl font-bold mb-4">Distribucion General</h2>
                            <Bar data={getGeneralData(selectedDataSet)} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Finance;
